import React, { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Shield, ClipboardList, Mail, Receipt, CreditCard, Monitor, BarChart3, Smartphone, Landmark, Info, CheckCircle2, XCircle, Lock, Phone, MapPin, KeyRound, Users, AlertTriangle } from 'lucide-react';

const PoliticaConfidentialitate = () => {
  const [showProcessors, setShowProcessors] = useState(false);

  // Scroll to top când pagina se încarcă
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-4xl mx-auto py-12 px-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <div className="flex items-center mb-8">
            <Shield className="w-12 h-12 text-emerald-600 mr-4" />
            <h1 className="text-4xl font-bold text-gray-900">Politica de Confidențialitate</h1>
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 mb-6">
              <strong>Data ultimei actualizări:</strong> 3 noiembrie 2026
            </p>

            <p className="text-gray-700 mb-8">
              BITCRAFTERS SRL ("noi", "noastră", "Operatorul") respectă confidențialitatea
              utilizatorilor săi și se angajează să protejeze datele cu caracter personal în conformitate cu{' '}
              <strong>Regulamentul General privind Protecția Datelor (GDPR - Regulamentul UE 2016/679)</strong> și legislația
              națională aplicabilă în România.
            </p>

            <div className="bg-blue-50 border-l-4 border-teal-500 p-4 mb-8">
              <p className="text-sm text-blue-800">
                <strong><ClipboardList className="w-4 h-4 inline-block align-text-bottom mr-1" aria-hidden="true" />Rezumat:</strong> Această politică descrie informațiile cu caracter personal pe care le colectăm de la
                dumneavoastră prin platforma <strong>cf-extras.ro</strong>, modul în care acestea sunt utilizate,
                cui sunt dezvăluite și drepturile de care dispuneți în acest sens.
              </p>
            </div>

            {/* 1. OPERATOR DE DATE */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Operator de Date cu Caracter Personal</h2>
            <p className="text-gray-700 mb-4">
              BITCRAFTERS SRL are calitatea de <strong>operator de date</strong> conform
              GDPR și respectă în totalitate prevederile Regulamentului (UE) 2016/679 privind protecția persoanelor fizice în ceea
              ce privește prelucrarea datelor cu caracter personal.
            </p>

            <div className="bg-gray-50 p-6 rounded-lg mb-6 border-2 border-gray-200">
              <p className="text-gray-700 mb-2">
                <strong>Operator:</strong> BITCRAFTERS SRL
              </p>
              <p className="text-gray-700">
                <strong>CUI:</strong> RO49900684
              </p>
              <p className="text-gray-700">
                <strong>Nr. Reg. Com.:</strong> J2024007383407
              </p>
              <p className="text-gray-700">
                <strong>Adresă:</strong> Str. Fratii Golescu 4, București, Sector 1
              </p>
              <p className="text-gray-700">
                <strong>Email contact GDPR:</strong>{' '}
                <a href="mailto:dpo@cf-extras.ro" className="text-emerald-600 hover:underline">
                  dpo@cf-extras.ro
                </a>
              </p>
              <p className="text-gray-700">
                <strong>Telefon:</strong>{' '}
                <a href="tel:+40756888282" className="text-emerald-600 hover:underline">
                  +40756888282
                </a>
              </p>
            </div>

            <p className="text-gray-700 mb-4">
              Pentru orice solicitare privind datele personale, exercitarea drepturilor GDPR sau nelămuriri cu privire la protecția
              datelor, ne puteți contacta la adresa de email de mai sus. <strong>Termenul de răspuns: maxim 30 zile</strong> de la
              primirea solicitării.
            </p>

            {/* 2. DEFINIȚII GDPR */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. Definiții Termeni GDPR</h2>
            <p className="text-gray-700 mb-4">
              În sensul prezentei Politici de Confidențialitate, următorii termeni au semnificațiile definite de{' '}
              <strong>Regulamentul (UE) 2016/679 (GDPR)</strong>:
            </p>

            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>
                <strong>Date cu caracter personal</strong> - orice informații referitoare la o persoană fizică identificată sau
                identificabilă. O persoană identificabilă este acea persoană care poate fi identificată, direct sau indirect, în mod
                particular prin referire la un număr de identificare (CNP, CUI) ori la unul sau mai mulți factori specifici
                identității sale fizice, fiziologice, economice sau sociale. Exemple: nume, prenume, CNP, adresă, email, telefon.
              </li>
              <li>
                <strong>Operator</strong> - persoana fizică sau juridică, autoritatea publică, agenția sau alt organism care, singur sau împreună cu altele, stabilește scopurile și mijloacele de
                prelucrare a datelor cu caracter personal; atunci când scopurile și mijloacele prelucrării sunt stabilite prin dreptul Uniunii sau dreptul intern, operatorul sau criteriile specifice pentru desemnarea acestuia pot fi prevăzute în dreptul Uniunii sau în dreptul intern. În cazul nostru: BITCRAFTERS SRL
              </li>
              <li>
                <strong>Persoană împuternicită / Procesator</strong> - o persoană fizică sau juridică care prelucr ează date cu
                caracter personal pe seama operatorului. Exemple: Stripe (plăți), NewsMAN (email), Twilio și SendSMS (SMS), Oblio (facturare).
              </li>
              <li>
                <strong>Persoană vizată</strong> - persoana ale cărei date cu caracter personal fac obiectul prelucrării. În cazul
                nostru: utilizatorul platformei cf-extras.ro.
              </li>
              <li>
                <strong>Prelucrarea datelor</strong> - orice operațiune sau set de operațiuni efectuate asupra datelor cu caracter
                personal, prin mijloace automate sau neautomate, cum ar fi: colectarea, înregistrarea, organizarea, stocarea, adaptarea,
                modificarea, extragerea, consultarea, utilizarea, dezvăluirea către terți prin transmitere, blocarea, ștergerea sau
                distrugerea.
              </li>
              <li>
                <strong>Consimțământ</strong> - orice indicație liberă, specifică, informată și lipsită de ambiguitate a dorinței
                persoanei vizate prin care aceasta, printr-o declarație sau printr-o acțiune clară afirmativă (ex: bifarea unui
                checkbox), exprimă acordul cu privire la prelucrarea datelor cu caracter personal care o privesc.
              </li>
              <li>
                <strong>Încălcarea datelor cu caracter personal</strong> - o încălcare a securității care duce la distrugerea,
                pierderea, modificarea, dezvăluirea neautorizată sau accesul la datele cu caracter personal.
              </li>
              <li>
                <strong>Restricționarea prelucrării</strong> - înseamnă marcarea datelor cu caracter personal stocate cu scopul de a limita prelucrarea viitoare a acestora.
              </li>
            </ul>

            {/* 3. CATEGORII DATE COLECTATE */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. Categorii de Date cu Caracter Personal Colectate</h2>
            <p className="text-gray-700 mb-4">
              Datele cu caracter personal pe care le prelucrăm pot include, dar nu se limitează la, următoarele categorii:
            </p>

            <div className="space-y-4 mb-6">
              <div className="bg-white border-2 border-gray-200 p-4 rounded-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-2"><ClipboardList className="w-5 h-5 inline-block align-text-bottom mr-1" aria-hidden="true" />Date de Identificare</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>
                    <strong>Date imobil:</strong> Județ, UAT (Unitate Administrativ-Teritorială), număr cadastral,
                    tip extras solicitat (Carte Funciară / Ortofotoplan)
                  </li>
                  <li>
                    <strong>Date facturare:</strong> CUI (pentru PJ), nume, prenume, adresă (pentru PF)
                  </li>
                </ul>
              </div>

              <div className="bg-white border-2 border-gray-200 p-4 rounded-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-2"><Mail className="w-5 h-5 inline-block align-text-bottom mr-1" aria-hidden="true" />Date de Contact</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Adresă email (pentru livrare extrase + facturi + comunicare)</li>
                  <li>Număr telefon mobil (pentru notificări SMS + confirmare comenzi)</li>
                </ul>
              </div>

              <div className="bg-white border-2 border-gray-200 p-4 rounded-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-2"><Receipt className="w-5 h-5 inline-block align-text-bottom mr-1" aria-hidden="true" />Date de Facturare</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>CUI/CNP pentru emitere factură fiscală</li>
                  <li>Denumire/Nume complet</li>
                  <li>Adresă de facturare completă</li>
                  <li>Cod poștal, județ, localitate</li>
                </ul>
              </div>

              <div className="bg-white border-2 border-gray-200 p-4 rounded-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-2"><CreditCard className="w-5 h-5 inline-block align-text-bottom mr-1" aria-hidden="true" />Date de Plată</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>
                    <strong>Procesate de Stripe</strong> (NU stocăm date cardului bancar pe serverele noastre!)
                  </li>
                  <li>Stripe ID tranzacție (pentru referință internă)</li>
                  <li>Status plată (succes/eșuat)</li>
                  <li>Sumă plătită + metodă plată</li>
                </ul>
              </div>

              <div className="bg-white border-2 border-gray-200 p-4 rounded-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-2"><Monitor className="w-5 h-5 inline-block align-text-bottom mr-1" aria-hidden="true" />Date Tehnice (Colectate Automat)</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Adresă IP (pentru securitate + analiză trafic)</li>
                  <li>Tip browser, versiune browser</li>
                  <li>Sistem de operare (Windows, macOS, Android, iOS)</li>
                  <li>Rezoluție ecran, limba browser</li>
                  <li>Cookies (session cookies + Google Analytics cookies - dacă sunt activate)</li>
                  <li>Data și ora vizitei, pagini accesate, durată vizită</li>
                </ul>
              </div>

              <div className="bg-white border-2 border-gray-200 p-4 rounded-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-2"><BarChart3 className="w-5 h-5 inline-block align-text-bottom mr-1" aria-hidden="true" />Date Comenzi și Tranzacții</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Număr comandă (generat automat)</li>
                  <li>Tip extras solicitat (Carte Funciară / Ortofotoplan)</li>
                  <li>Status comandă (pending, processing, completed, failed)</li>
                  <li>Istoric comenzi</li>
                  <li>Comunicări prin email/SMS legate de comandă</li>
                </ul>
              </div>
            </div>

            {/* 4. SCOPURI ȘI TEMEI JURIDIC */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. Scopurile și Temeiul Juridic al Prelucrării</h2>
            <p className="text-gray-700 mb-4">
              Datele dumneavoastră cu caracter personal sunt prelucrate în baza consimțământului, interesului legitim sau obligațiilor
              legale, conform Regulamentului (UE) 2016/679 (GDPR), pentru următoarele scopuri:
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="min-w-full border-collapse border-2 border-gray-300">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">Scop Prelucrare</th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">Temei Juridic GDPR</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr>
                    <td className="border border-gray-300 px-4 py-3">
                      Procesarea comenzilor și gestionarea relației contractuale (validare număr cadastral, completare formular, plasare comandă)
                    </td>
                    <td className="border border-gray-300 px-4 py-3">
                      <strong>Executarea contractului</strong> (Art. 6(1)(b) GDPR)
                    </td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-4 py-3">
                      Emiterea extraselor de carte funciară prin ANCPI
                    </td>
                    <td className="border border-gray-300 px-4 py-3">
                      <strong>Executarea contractului</strong> (Art. 6(1)(b) GDPR)
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-3">
                      Emiterea facturilor fiscale (Oblio) și respectarea obligațiilor contabile/fiscale
                    </td>
                    <td className="border border-gray-300 px-4 py-3">
                      <strong>Obligație legală</strong> (Art. 6(1)(c) GDPR - Codul Fiscal)
                    </td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-4 py-3">
                      Livrarea extraselor prin email (NewsMAN) + notificări SMS (Twilio / SendSMS)
                    </td>
                    <td className="border border-gray-300 px-4 py-3">
                      <strong>Executarea contractului</strong> (Art. 6(1)(b) GDPR)
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-3">
                      Procesarea plăților cu cardul prin Stripe (SSL 256-bit encryption)
                    </td>
                    <td className="border border-gray-300 px-4 py-3">
                      <strong>Executarea contractului</strong> (Art. 6(1)(b) GDPR)
                    </td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-4 py-3">
                      Arhivarea comenzilor și facturilor (10 ani conform legislației fiscale)
                    </td>
                    <td className="border border-gray-300 px-4 py-3">
                      <strong>Obligație legală</strong> (Art. 6(1)(c) GDPR - Codul Fiscal Art. 11)
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-3">
                      Comunicări de marketing (oferte, promoții, newsletter) - DOAR dacă ați bifat checkbox-ul opt-in
                    </td>
                    <td className="border border-gray-300 px-4 py-3">
                      <strong>Consimțământ explicit</strong> (Art. 6(1)(a) GDPR - retras oricând)
                    </td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-4 py-3">
                      Îmbunătățirea serviciilor, analiză trafic (Google Analytics), sondaje satisfacție
                    </td>
                    <td className="border border-gray-300 px-4 py-3">
                      <strong>Interes legitim</strong> (Art. 6(1)(f) GDPR)
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-3">
                      Asigurarea securității platformei, prevenirea fraudelor, logging activități
                    </td>
                    <td className="border border-gray-300 px-4 py-3">
                      <strong>Interes legitim</strong> (Art. 6(1)(f) GDPR)
                    </td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-4 py-3">
                      Furnizarea de consultanță și suport tehnic (email, telefon)
                    </td>
                    <td className="border border-gray-300 px-4 py-3">
                      <strong>Executarea contractului</strong> (Art. 6(1)(b) GDPR)
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-gray-700 mb-4">
              <strong>Important:</strong> Consimțământul dumneavoastră pentru marketing este considerat ca fiind acordat doar dacă ați
              bifat explicit checkbox-ul <strong>"Accept comunicări marketing (oferte, promoții)"</strong> în formularul de comandă
              sau bifa de acord din fereastra „Beneficiu exclusiv” (codul de reducere trimis pe email), unde păstrăm textul acordului și data la care l-ați dat.
              Puteți retrage consimțământul oricând prin click pe link-ul de dezabonare din email-uri sau prin solicitare la contact@cf-extras.ro.
            </p>

            {/* 5. DESTINATARI ȘI PROCESATORI */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              5. Destinatari și Categorii de Destinatari ai Datelor cu Caracter Personal
            </h2>
            <p className="text-gray-700 mb-4">
              Colaborăm cu un număr select de furnizori și parteneri de încredere pentru anumite servicii de procesare, stocare și
              livrare a datelor. Acești furnizori (persoane împuternicite) sunt selectați cu atenție și îndeplinesc standarde înalte
              de protecție GDPR. Împărtășim doar informațiile necesare pentru serviciile oferite și îi obligăm contractual să păstreze
              confidențialitatea datelor.
            </p>

            <button
              onClick={() => setShowProcessors(!showProcessors)}
              className="text-emerald-600 hover:text-blue-800 hover:underline font-medium mb-4 flex items-center gap-2 transition-colors"
            >
              <span>{showProcessors ? '▼' : '►'}</span>
              <span>Lista completă a procesatorilor autorizați (Stripe, NewsMAN, Twilio, SendSMS, Oblio, ANCPI, Google Analytics, Cloudflare)</span>
            </button>

            {showProcessors && (
              <div className="space-y-4 mb-6">
              <div className="bg-white border-2 border-blue-200 p-4 rounded-lg">
                <h3 className="text-lg font-bold text-blue-900 mb-2"><CreditCard className="w-5 h-5 inline-block align-text-bottom mr-1" aria-hidden="true" />Stripe Inc. (SUA)</h3>
                <p className="text-gray-700 mb-2">
                  <strong>Scop:</strong> Procesare plăți cu cardul bancar (Visa, Mastercard, American Express)
                </p>
                <p className="text-gray-700 mb-2">
                  <strong>Date prelucrate:</strong> Detalii card (criptate SSL 256-bit, NU stocăm pe serverele noastre!), suma tranzacției,
                  email, nume
                </p>
                <p className="text-gray-700 mb-2">
                  <strong>Transfer internațional:</strong> SUA - transfer legal prin <strong>Standard Contractual Clauses (SCCs)</strong>{' '}
                  aprobate de Comisia Europeană
                </p>
                <p className="text-gray-700">
                  <strong>Politica Stripe:</strong>{' '}
                  <a
                    href="https://stripe.com/privacy"
                    className="text-emerald-600 hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://stripe.com/privacy
                  </a>
                </p>
              </div>

              <div className="bg-white border-2 border-green-200 p-4 rounded-lg">
                <h3 className="text-lg font-bold text-green-900 mb-2"><Mail className="w-5 h-5 inline-block align-text-bottom mr-1" aria-hidden="true" />NewsMAN - Dazoot Software SRL (România)</h3>
                <p className="text-gray-700 mb-2">
                  <strong>Scop:</strong> Livrare email tranzacțional cu atașamente PDF (extras carte funciară + facturi) și
                  reamintirea pentru comenzile nefinalizate
                </p>
                <p className="text-gray-700 mb-2">
                  <strong>Date prelucrate:</strong> Email, nume, conținutul emailului și atașamentele PDF (extrase + facturi),
                  evenimente de livrare (livrat, deschis, click pe link, bounce)
                </p>
                <p className="text-gray-700 mb-2">
                  <strong>Locație:</strong> România. Trimiterea tehnică a emailurilor poate folosi infrastructura furnizorilor
                  NewsMAN; eventualele transferuri în afara SEE sunt acoperite de clauzele GDPR dintre NewsMAN și furnizorii săi
                </p>
                <p className="text-gray-700">
                  <strong>Politica NewsMAN:</strong>{' '}
                  <a
                    href="https://www.newsman.com/privacy"
                    className="text-emerald-600 hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://www.newsman.com/privacy
                  </a>
                </p>
              </div>

              <div className="bg-white border-2 border-purple-200 p-4 rounded-lg">
                <h3 className="text-lg font-bold text-purple-900 mb-2"><Smartphone className="w-5 h-5 inline-block align-text-bottom mr-1" aria-hidden="true" />Twilio Inc. (SUA)</h3>
                <p className="text-gray-700 mb-2">
                  <strong>Scop:</strong> Livrare SMS notificări (confirmare comandă, status procesare, certificat livrat)
                </p>
                <p className="text-gray-700 mb-2">
                  <strong>Date prelucrate:</strong> Număr telefon mobil, text mesaj SMS
                </p>
                <p className="text-gray-700 mb-2">
                  <strong>Transfer internațional:</strong> SUA - transfer legal prin <strong>Standard Contractual Clauses (SCCs)</strong>
                </p>
                <p className="text-gray-700">
                  <strong>Politica Twilio:</strong>{' '}
                  <a
                    href="https://www.twilio.com/legal/privacy"
                    className="text-emerald-600 hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://www.twilio.com/legal/privacy
                  </a>
                </p>
              </div>

              <div className="bg-white border-2 border-purple-200 p-4 rounded-lg">
                <h3 className="text-lg font-bold text-purple-900 mb-2"><Smartphone className="w-5 h-5 inline-block align-text-bottom mr-1" aria-hidden="true" />sendSMS Solutions SRL (România)</h3>
                <p className="text-gray-700 mb-2">
                  <strong>Scop:</strong> Livrare SMS notificări - furnizor de rezervă, folosit când Twilio nu este disponibil
                </p>
                <p className="text-gray-700 mb-2">
                  <strong>Date prelucrate:</strong> Număr telefon mobil, text mesaj SMS
                </p>
                <p className="text-gray-700 mb-2">
                  <strong>Locație:</strong> <strong>România</strong> (date stocate în România, fără transfer în afara SEE)
                </p>
                <p className="text-gray-700">
                  <strong>Politica SendSMS:</strong>{' '}
                  <a
                    href="https://www.sendsms.ro/ro/gdpr/"
                    className="text-emerald-600 hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://www.sendsms.ro/ro/gdpr/
                  </a>
                </p>
              </div>

              <div className="bg-white border-2 border-yellow-200 p-4 rounded-lg">
                <h3 className="text-lg font-bold text-yellow-900 mb-2"><Receipt className="w-5 h-5 inline-block align-text-bottom mr-1" aria-hidden="true" />Oblio (România)</h3>
                <p className="text-gray-700 mb-2">
                  <strong>Scop:</strong> Emitere automată facturi fiscale (cu TVA)
                </p>
                <p className="text-gray-700 mb-2">
                  <strong>Date prelucrate:</strong> CUI/CNP facturare, denumire/nume, adresă facturare, sumă + TVA
                </p>
                <p className="text-gray-700 mb-2">
                  <strong>Locație:</strong> <strong>România</strong> (procesator local, fără transfer internațional)
                </p>
                <p className="text-gray-700">
                  <strong>Website Oblio:</strong>{' '}
                  <a
                    href="https://www.oblio.eu"
                    className="text-emerald-600 hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://www.oblio.eu
                  </a>
                </p>
              </div>

              <div className="bg-white border-2 border-red-200 p-4 rounded-lg">
                <h3 className="text-lg font-bold text-red-900 mb-2"><Landmark className="w-5 h-5 inline-block align-text-bottom mr-1" aria-hidden="true" />ANCPI (România)</h3>
                <p className="text-gray-700 mb-2">
                  <strong>Scop:</strong> Validare nr. cadastral + emitere extrase carte funciară oficiale
                </p>
                <p className="text-gray-700 mb-2">
                  <strong>Date prelucrate:</strong> Județ, UAT, nr. cadastral, tip extras solicitat (CF/PC)
                </p>
                <p className="text-gray-700 mb-2">
                  <strong>Statut:</strong> <strong>Autoritate publică</strong> (nu este procesator, ci destinatar legal al datelor)
                </p>
                <p className="text-gray-700">
                  <strong>Website ANCPI:</strong>{' '}
                  <a
                    href="https://www.ancpi.ro"
                    className="text-emerald-600 hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://www.ancpi.ro
                  </a>
                </p>
              </div>

              <div className="bg-white border-2 border-gray-200 p-4 rounded-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-2"><BarChart3 className="w-5 h-5 inline-block align-text-bottom mr-1" aria-hidden="true" />Google Analytics (Google Ireland Limited, UE)</h3>
                <p className="text-gray-700 mb-2">
                  <strong>Scop:</strong> Analiză trafic website, îmbunătățire experiență utilizator (dacă cookies analytics sunt acceptate)
                </p>
                <p className="text-gray-700 mb-2">
                  <strong>Date prelucrate:</strong> IP anonim (anonimizat), pagini vizitate, durată vizită, browser, OS
                </p>
                <p className="text-gray-700 mb-2">
                  <strong>Locație:</strong> <strong>Google Ireland Limited</strong> (Dublin, Irlanda - Uniunea Europeană)
                </p>
                <p className="text-gray-700">
                  <strong>Politica Google:</strong>{' '}
                  <a
                    href="https://policies.google.com/privacy"
                    className="text-emerald-600 hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://policies.google.com/privacy
                  </a>
                </p>
              </div>

              <div className="bg-white border-2 border-orange-200 p-4 rounded-lg">
                <h3 className="text-lg font-bold text-orange-900 mb-2"><Shield className="w-5 h-5 inline-block align-text-bottom mr-1" aria-hidden="true" />Cloudflare Inc. (SUA)</h3>
                <p className="text-gray-700 mb-2">
                  <strong>Scop:</strong> Protecție DDoS, CDN (Content Delivery Network), SSL/TLS, securitate platformă, optimizare performanță
                </p>
                <p className="text-gray-700 mb-2">
                  <strong>Date prelucrate:</strong> Adresă IP, cookies securitate, request metadata (browser, OS, User-Agent), log-uri acces server
                </p>
                <p className="text-gray-700 mb-2">
                  <strong>Transfer internațional:</strong> SUA - transfer legal prin <strong>Standard Contractual Clauses (SCCs)</strong>
                </p>
                <p className="text-gray-700">
                  <strong>Politica Cloudflare:</strong>{' '}
                  <a
                    href="https://www.cloudflare.com/privacypolicy/"
                    className="text-emerald-600 hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://www.cloudflare.com/privacypolicy/
                  </a>
                </p>
              </div>
            </div>
            )}

            <p className="text-gray-700 mb-4">
              <strong>Alte categorii de destinatari (conform legislației):</strong>
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>
                <strong>Autorități publice și instituții ale statului român:</strong> ANAF (Agenția Națională de Administrare Fiscală),
                ANSPDCP (Autoritatea Națională de Supraveghere GDPR), instanțe judecătorești - doar la cerere legală
              </li>
              <li>
                <strong>Contabili, auditori, avocați, consultanți externi:</strong> doar pentru îndeplinirea obligațiilor legale
                (contabilitate, audit fiscal, consultanță juridică)
              </li>
            </ul>

            {/* 6. TRANSFER INTERNAȚIONAL */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. Transfer Internațional de Date (SUA)</h2>
            <p className="text-gray-700 mb-4">
              Unii procesatori autorizați (Stripe, Twilio) sunt localizați în <strong>Statele Unite ale Americii</strong>.
              Pentru a asigura protecția datelor dumneavoastră în conformitate cu GDPR, am implementat <strong>garanții adecvate</strong>
              prin:
            </p>

            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>
                <strong>Standard Contractual Clauses (SCCs)</strong> - clauze contractuale standard aprobate de Comisia Europeană
                (Decizia 2021/914), care impun aceleași obligații de protecție a datelor ca și în Uniunea Europeană
              </li>
              <li>
                <strong>Măsuri tehnice suplimentare:</strong> criptare SSL/TLS end-to-end, pseudonimizare date, acces restricționat
              </li>
              <li>
                <strong>Certificări de securitate:</strong> SOC 2 Type II, ISO 27001, PCI DSS Level 1 (pentru Stripe plăți card)
              </li>
            </ul>

            <p className="text-gray-700 mb-4">
              Aceste garanții asigură că datele dumneavoastră beneficiază de un nivel echivalent de protecție cu cel oferit de GDPR,
              chiar dacă sunt transferate în afara Spațiului Economic European (SEE).
            </p>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-6">
              <p className="text-sm text-yellow-800">
                <strong><Info className="w-4 h-4 inline-block align-text-bottom mr-1" aria-hidden="true" />Notă:</strong> Pentru detalii complete despre transferurile internaționale și garanțiile implementate,
                puteți consulta politicile de confidențialitate ale procesatorilor (link-uri mai sus) sau ne puteți contacta la dpo@cf-extras.ro.
              </p>
            </div>

            {/* 7. PERIOADA STOCARE */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. Perioada de Stocare a Datelor</h2>
            <p className="text-gray-700 mb-4">
              Păstrăm datele dumneavoastră cu caracter personal pentru o perioadă de timp necesară îndeplinirii scopurilor indicate sau
              pe durata relației contractuale. Vom înceta să le prelucrăm când nu mai sunt necesare ori când vă retrageți consimțământul
              (dacă este cazul) și nu mai există motive care justifică prelucrarea acestora în continuare.
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="min-w-full border-collapse border-2 border-gray-300">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">Categorie Date</th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">Perioada Stocare</th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">Temei Legal</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr>
                    <td className="border border-gray-300 px-4 py-3">Date comenzi (nr. cadastral, extrase, facturi)</td>
                    <td className="border border-gray-300 px-4 py-3">
                      <strong>5 ani</strong> (calculați de la data de 1 iulie a anului următor)
                    </td>
                    <td className="border border-gray-300 px-4 py-3">Obligație legală (Codul Fiscal Art. 11 alin. 3)</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-4 py-3">
                      Facturi fiscale (PDF arhivate backend/facturi/)
                    </td>
                    <td className="border border-gray-300 px-4 py-3">
                      <strong>5 ani</strong> (calculați de la data de 1 iulie a anului următor)
                    </td>
                    <td className="border border-gray-300 px-4 py-3">Obligație legală fiscală</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-3">Date plată Stripe (transaction ID, status)</td>
                    <td className="border border-gray-300 px-4 py-3">
                      <strong>5 ani</strong> (calculați de la data de 1 iulie a anului următor)
                    </td>
                    <td className="border border-gray-300 px-4 py-3">Obligație legală contabilă</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-4 py-3">Date contact (email, telefon) - marketing</td>
                    <td className="border border-gray-300 px-4 py-3">
                      <strong>Până la retragerea consimțământului</strong>
                    </td>
                    <td className="border border-gray-300 px-4 py-3">Consimțământ explicit (retras oricând)</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-3">
                      Abonare la oferte pe email (popup cadou): adresa de email, textul și data acordului, codul de reducere trimis
                    </td>
                    <td className="border border-gray-300 px-4 py-3">
                      <strong>Până la dezabonare</strong> (link în fiecare email sau la contact@cf-extras.ro)
                    </td>
                    <td className="border border-gray-300 px-4 py-3">Consimțământ explicit (retras oricând)</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-3">
                      Comenzi nefinalizate (date imobil, facturare și contact introduse până la pasul de confirmare, fără plată)
                    </td>
                    <td className="border border-gray-300 px-4 py-3">
                      <strong>7 zile</strong> (ștergere automată)
                    </td>
                    <td className="border border-gray-300 px-4 py-3">
                      Interes legitim - o singură reamintire pe email pentru finalizarea comenzii, cu posibilitate de dezabonare
                    </td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-4 py-3">Date tehnice (IP, browser, OS)</td>
                    <td className="border border-gray-300 px-4 py-3">
                      <strong>12 luni</strong>
                    </td>
                    <td className="border border-gray-300 px-4 py-3">Interes legitim (securitate + analiză)</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-3">Cookies sesiune (session cookies)</td>
                    <td className="border border-gray-300 px-4 py-3">
                      <strong>Până la închidere browser</strong>
                    </td>
                    <td className="border border-gray-300 px-4 py-3">Necesar funcționare platformă</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-4 py-3">Cookies Google Analytics (_ga, _gid)</td>
                    <td className="border border-gray-300 px-4 py-3">
                      <strong>2 ani (_ga), 1 zi (_gid)</strong>
                    </td>
                    <td className="border border-gray-300 px-4 py-3">Consimțământ cookies (banner)</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-3">Logs comenzi (backend/logs/orders/)</td>
                    <td className="border border-gray-300 px-4 py-3">
                      <strong>5 ani</strong> (calculați de la data de 1 iulie a anului următor)
                    </td>
                    <td className="border border-gray-300 px-4 py-3">Obligație legală + debugging</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-gray-700 mb-4">
              <strong>Important:</strong> După expirarea perioadei de stocare, datele vor fi șterse automat sau anonimizate (transformate
              în date statistice agregate fără legătură cu persoane identificabile).
            </p>

            {/* 8. DREPTURILE PERSOANELOR VIZATE */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. Drepturile Persoanelor Vizate (Drepturile Dumneavoastră)</h2>
            <p className="text-gray-700 mb-4">
              Conform <strong>Regulamentului GDPR (UE 2016/679)</strong>, aveți următoarele drepturi cu privire la datele dumneavoastră
              cu caracter personal:
            </p>

            <div className="space-y-4 mb-6">
              <div className="bg-white border-l-4 border-teal-500 p-4">
                <h3 className="text-lg font-bold text-blue-900 mb-2"><span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-600 text-white text-xs font-bold mr-2 align-middle">1</span>Dreptul de Acces (Art. 15 GDPR)</h3>
                <p className="text-gray-700">
                  Dreptul de a obține o confirmare din partea noastră că prelucrăm sau nu datele dvs. cu caracter personal și, în caz
                  afirmativ, acces la datele respective și la informații privind modalitatea în care sunt prelucrate. Informațiile vă vor
                  fi oferite <strong>gratuit (1x/an)</strong>, în scris sau electronic. Termenul de răspuns: <strong>maxim 30 zile</strong>.
                </p>
              </div>

              <div className="bg-white border-l-4 border-green-500 p-4">
                <h3 className="text-lg font-bold text-green-900 mb-2"><span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-600 text-white text-xs font-bold mr-2 align-middle">2</span>Dreptul la Rectificare (Art. 16 GDPR)</h3>
                <p className="text-gray-700">
                  Dreptul de a solicita corectarea, fără întârzieri nejustificate, a datelor cu caracter personal inexacte. De exemplu:
                  schimbare adresă email, corectare nume, actualizare telefon. Rectificarea va fi comunicată fiecărui destinatar (procesator)
                  la care au fost transmise datele.
                </p>
              </div>

              <div className="bg-white border-l-4 border-red-500 p-4">
                <h3 className="text-lg font-bold text-red-900 mb-2"><span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-600 text-white text-xs font-bold mr-2 align-middle">3</span>Dreptul la Ștergere - "Dreptul de a fi Uitat" (Art. 17 GDPR)</h3>
                <p className="text-gray-700 mb-2">
                  Dreptul de a solicita ștergerea datelor cu caracter personal, fără întârzieri nejustificate, <strong>DAR cu excepții</strong>:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>
                    <CheckCircle2 className="w-4 h-4 inline-block align-text-bottom mr-1 text-green-600" aria-hidden="true" /><strong>Putem șterge:</strong> dacă nu mai există raport contractual, ați retras consimțământul pentru marketing
                  </li>
                  <li>
                    <XCircle className="w-4 h-4 inline-block align-text-bottom mr-1 text-red-600" aria-hidden="true" /><strong>NU putem șterge:</strong> date comenzi + facturi (obligație legală 5 ani calculați de la data de 1 iulie a anului următor celui încheierii exercițiului financiar în care au fost întocmite), date necesare
                    pentru constatarea/exercitarea/apărarea unui drept în instanță
                  </li>
                </ul>
              </div>

              <div className="bg-white border-l-4 border-yellow-500 p-4">
                <h3 className="text-lg font-bold text-yellow-900 mb-2"><span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-600 text-white text-xs font-bold mr-2 align-middle">4</span>Dreptul la Restricționarea Prelucrării (Art. 18 GDPR)</h3>
                <p className="text-gray-700">
                  Dreptul de a solicita restricționarea prelucrării în anumite situații: (1) contestați exactitatea datelor (restricționare
                  până la verificare), (2) prelucrarea este ilegală dar nu doriți ștergerea, (3) operatorul nu mai are nevoie de datele cu caracter personal în scopul prelucrării, dar persoana vizată i le solicită pentru constatarea, exercitarea sau apărarea unui drept în instanță, (4) v-ați opus prelucrării (restricționare
                  până la verificarea drepturilor legitime ale operatorului).
                </p>
              </div>

              <div className="bg-white border-l-4 border-purple-500 p-4">
                <h3 className="text-lg font-bold text-purple-900 mb-2"><span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-600 text-white text-xs font-bold mr-2 align-middle">5</span>Dreptul la Portabilitatea Datelor (Art. 20 GDPR)</h3>
                <p className="text-gray-700">
                  Dreptul de a primi datele personale pe care ni le-ați furnizat într-un <strong>format structurat, utilizat în mod curent
                  și care poate fi citit automat</strong> (ex: CSV, JSON, PDF). De asemenea, aveți dreptul ca aceste date să fie transmise
                  direct altui operator (dacă este fezabil tehnic).
                </p>
              </div>

              <div className="bg-white border-l-4 border-orange-500 p-4">
                <h3 className="text-lg font-bold text-orange-900 mb-2"><span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-600 text-white text-xs font-bold mr-2 align-middle">6</span>Dreptul la Opoziție (Art. 21 GDPR)</h3>
                <p className="text-gray-700 mb-2">
                  Dreptul de a vă opune prelucrării datelor personale, în special pentru:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>
                    <strong>Marketing direct:</strong> vă puteți opune oricând (click dezabonare în email sau solicitare la contact@cf-extras.ro)
                  </li>
                  <li>
                    <strong>Interes legitim:</strong> puteți contesta prelucrarea bazată pe interesul legitim (vom analiza cererea
                    și vom opri prelucrarea dacă nu există motive legitime imperative și care prevalează asupra intereselor, drepturilor și libertăților persoanei vizate sau că scopul este constatarea, exercitarea sau apărarea unui drept în instanță)
                  </li>
                </ul>
              </div>

              <div className="bg-white border-l-4 border-indigo-500 p-4">
                <h3 className="text-lg font-bold text-indigo-900 mb-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-600 text-white text-xs font-bold mr-2 align-middle">7</span>Dreptul de a nu face obiectul unei decizii bazate exclusiv pe prelucrarea automată (Art. 22 GDPR)
                </h3>
                <p className="text-gray-700">
                  Persoana vizată are dreptul de a nu face obiectul unei decizii bazate exclusiv pe prelucrarea automată, inclusiv crearea de profiluri, care produce efecte juridice care privesc persoana vizată sau o afectează în mod similar într-o măsură semnificativă.
                </p>
              </div>

              <div className="bg-white border-l-4 border-pink-500 p-4">
                <h3 className="text-lg font-bold text-pink-900 mb-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-600 text-white text-xs font-bold mr-2 align-middle">8</span>Dreptul de a Depune Plângere la ANSPDCP (Art. 77 GDPR)
                </h3>
                <p className="text-gray-700">
                  Dacă considerați că drepturile dumneavoastră GDPR au fost încălcate, aveți dreptul de a depune o plângere la{' '}
                  <strong>Autoritatea Națională de Supraveghere a Prelucrării Datelor cu Caracter Personal (ANSPDCP)</strong> - date
                  contact în secțiunea 15 mai jos.
                </p>
              </div>

              <div className="bg-white border-l-4 border-gray-500 p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-2"><span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-600 text-white text-xs font-bold mr-2 align-middle">9</span>Retragerea Consimțământului (Marketing)</h3>
                <p className="text-gray-700">
                  Dacă ați bifat checkbox-ul pentru comunicări marketing, puteți retrage consimțământul <strong>oricând</strong>,
                  fără a afecta legalitatea prelucrării efectuate până la retragere. Metode de retragere: (1) Click link "Dezabonare"
                  din footer email-uri marketing, (2) Email la dpo@cf-extras.ro, (3) Telefon la +40756888282.
                </p>
              </div>
            </div>

            {/* 9. EXERCITAREA DREPTURILOR */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. Exercitarea Drepturilor GDPR</h2>
            <p className="text-gray-700 mb-4">
              Pentru a vă exercita drepturile menționate mai sus, ne puteți contacta prin:
            </p>

            <div className="bg-blue-50 p-6 rounded-lg border-2 border-blue-200 mb-6">
              <p className="text-gray-700 mb-2">
                <strong><Mail className="w-4 h-4 inline-block align-text-bottom mr-1" aria-hidden="true" />Email (modalitate preferată):</strong>{' '}
                <a
                  href="mailto:contact@cf-extras.ro"
                  className="text-emerald-600 hover:underline font-semibold"
                >
                  contact@cf-extras.ro
                </a>
              </p>
              <p className="text-gray-700 mb-2">
                <strong><Lock className="w-4 h-4 inline-block align-text-bottom mr-1" aria-hidden="true" />Responsabil Protecția Datelor (DPO):</strong>{' '}
                <a
                  href="mailto:dpo@cf-extras.ro"
                  className="text-emerald-600 hover:underline font-semibold"
                >
                  dpo@cf-extras.ro
                </a>
              </p>
              <p className="text-gray-700 mb-2">
                <strong><Phone className="w-4 h-4 inline-block align-text-bottom mr-1" aria-hidden="true" />Telefon:</strong>{' '}
                <a href="tel:+40756888282" className="text-emerald-600 hover:underline font-semibold">
                  +40756888282
                </a>
              </p>
              <p className="text-gray-700">
                <strong><MapPin className="w-4 h-4 inline-block align-text-bottom mr-1" aria-hidden="true" />Adresă poștală:</strong> Str. Fratii Golescu 4, București, Sector 1
              </p>
            </div>

            <p className="text-gray-700 mb-4">
              <strong>Procedură exercitare drepturi:</strong>
            </p>
            <ol className="list-decimal pl-6 text-gray-700 mb-4 space-y-2">
              <li>
                <strong>Trimiteți cererea</strong> pe email (modalitate preferată) sau poștă, specificând:
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Dreptul pe care doriți să îl exercitați (acces, rectificare, ștergere, etc.)</li>
                  <li>Datele de identificare (nume, prenume, email, telefon folosit la comandă)</li>
                  <li>Număr comandă (dacă este disponibil)</li>
                </ul>
              </li>
              <li>
                <strong>Verificare identitate:</strong> pentru conformare cu obligațiile de securitate GDPR, este posibil să vă cerem
                dovezi suplimentare pentru identificare (copie CI, selfie cu CI, email de confirmare comandă)
              </li>
              <li>
                <strong>Procesare cerere:</strong> vom analiza cererea dumneavoastră și vom răspunde în termen de <strong>maxim 30 zile</strong>{' '}
                de la primirea solicitării. Această perioadă poate fi prelungită cu <strong>încă 2 luni</strong> dacă cererea este
                complexă (vă vom informa despre prelungire în primele 30 zile)
              </li>
              <li>
                <strong>Răspuns:</strong> veți primi răspunsul pe email sau poștă (conform solicitării), gratuit (prima solicitare pe an)
              </li>
            </ol>

            <p className="text-gray-700 mb-4">
              <strong>Important:</strong> Ne rezervăm dreptul de a refuza cereri vădite excesive (repetitive, nejustificate) sau de a
              percepe o taxă rezonabilă pentru cost administrativ (conform Art. 12(5) GDPR).
            </p>

            {/* 10. SECURITATEA DATELOR */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">10. Măsuri de Securitate și Protecție a Datelor</h2>
            <p className="text-gray-700 mb-4">
              Implementăm măsuri tehnice și organizatorice adecvate pentru protejarea datelor dumneavoastră împotriva distrugerii
              accidentale sau ilegale, pierderii, alterării, dezvăluirii sau accesului neautorizat:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-white border-2 border-gray-200 p-4 rounded-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-2"><KeyRound className="w-5 h-5 inline-block align-text-bottom mr-1" aria-hidden="true" />Măsuri Tehnice</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>
                    <strong>Criptare SSL/TLS 256-bit:</strong> toată comunicarea între browser și server
                  </li>
                  <li>
                    <strong>HTTPS Strict:</strong> redirecționare automată HTTP → HTTPS
                  </li>
                  <li>
                    <strong>Hashing parole:</strong> bcrypt (NU stocăm parole în clar)
                  </li>
                  <li>
                    <strong>Stripe PCI DSS Level 1:</strong> plăți card (NU stocăm date card!)
                  </li>
                  <li>
                    <strong>Backup automat:</strong> zilnic, criptat, stocare redundantă
                  </li>
                  <li>
                    <strong>Firewall:</strong> protecție DDoS, rate limiting
                  </li>
                  <li>
                    <strong>Logging activități:</strong> monitorizare acces neautorizat
                  </li>
                </ul>
              </div>

              <div className="bg-white border-2 border-gray-200 p-4 rounded-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-2"><Users className="w-5 h-5 inline-block align-text-bottom mr-1" aria-hidden="true" />Măsuri Organizatorice</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>
                    <strong>Acces restricționat:</strong> doar personal autorizat (role-based access)
                  </li>
                  <li>
                    <strong>Confidențialitate angajați:</strong> contract + formare GDPR
                  </li>
                  <li>
                    <strong>Audit periodic:</strong> review măsuri securitate (anual)
                  </li>
                  <li>
                    <strong>Update software:</strong> patch-uri securitate regulate
                  </li>
                  <li>
                    <strong>Plan răspuns incidente:</strong> proceduri breach notification
                  </li>
                  <li>
                    <strong>Contracte procesatori:</strong> DPA (Data Processing Agreements)
                  </li>
                  <li>
                    <strong>Minimizare date:</strong> colectăm doar strict necesarul
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
              <p className="text-sm text-red-800">
                <strong><AlertTriangle className="w-4 h-4 inline-block align-text-bottom mr-1" aria-hidden="true" />Încălcare date (Data Breach):</strong> În cazul improbabil al unei încălcări de securitate care poate prezenta
                un risc ridicat pentru drepturile și libertățile dumneavoastră, vă vom notifica în termen de <strong>72 ore</strong> de
                la constatare, conform Art. 34 GDPR. De asemenea, vom notifica <strong>ANSPDCP</strong> conform Art. 33 GDPR.
              </p>
            </div>

            {/* 11. COOKIES */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">11. Politica Cookies</h2>
            <p className="text-gray-700 mb-4">
              Site-ul <strong>cf-extras.ro</strong> utilizează cookie-uri pentru a îmbunătăți experiența de navigare.
              Cookie-urile sunt fișiere mici (&lt;4KB) salvate pe dispozitivul dumneavoastră care ne ajută să optimizăm funcționarea
              site-ului.
            </p>

            <p className="text-gray-700 mb-4">
              <strong>Categorii de cookies utilizate:</strong>
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="min-w-full border-collapse border-2 border-gray-300">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">Cookie</th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">Tip</th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">Durata</th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-bold text-gray-900">Scop</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr>
                    <td className="border border-gray-300 px-4 py-3">
                      <strong>connect.sid</strong>
                    </td>
                    <td className="border border-gray-300 px-4 py-3">HTTP</td>
                    <td className="border border-gray-300 px-4 py-3">Session (închidere browser)</td>
                    <td className="border border-gray-300 px-4 py-3">
                      <strong>Esențial:</strong> sesiune utilizator, autentificare admin
                    </td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-4 py-3">
                      <strong>_ga</strong>
                    </td>
                    <td className="border border-gray-300 px-4 py-3">HTTP</td>
                    <td className="border border-gray-300 px-4 py-3">2 ani</td>
                    <td className="border border-gray-300 px-4 py-3">
                      <strong>Analiză:</strong> Google Analytics - ID unic vizitator (anonim)
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-3">
                      <strong>_gid</strong>
                    </td>
                    <td className="border border-gray-300 px-4 py-3">HTTP</td>
                    <td className="border border-gray-300 px-4 py-3">1 zi</td>
                    <td className="border border-gray-300 px-4 py-3">
                      <strong>Analiză:</strong> Google Analytics - sesiune vizită
                    </td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-4 py-3">
                      <strong>_gat</strong>
                    </td>
                    <td className="border border-gray-300 px-4 py-3">HTTP</td>
                    <td className="border border-gray-300 px-4 py-3">1 minut</td>
                    <td className="border border-gray-300 px-4 py-3">
                      <strong>Analiză:</strong> Google Analytics - throttling requests
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-3">
                      <strong>CookieConsent</strong>
                    </td>
                    <td className="border border-gray-300 px-4 py-3">HTTP</td>
                    <td className="border border-gray-300 px-4 py-3">1 an</td>
                    <td className="border border-gray-300 px-4 py-3">
                      <strong>Esențial:</strong> stocheaza consimțământ cookie banner
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-gray-700 mb-4">
              <strong>Gestionare cookies:</strong>
            </p>
            <p className="text-gray-700 mb-4">
              Puteți bloca sau șterge cookie-urile prin setările browserului dumneavoastră. Instrucțiuni:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
              <li>
                <strong>Google Chrome:</strong>{' '}
                <a
                  href="https://support.google.com/chrome/answer/95647"
                  className="text-emerald-600 hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  support.google.com/chrome/answer/95647
                </a>
              </li>
              <li>
                <strong>Mozilla Firefox:</strong>{' '}
                <a
                  href="https://support.mozilla.org/ro/kb/cookie-urile"
                  className="text-emerald-600 hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  support.mozilla.org/ro/kb/cookie-urile
                </a>
              </li>
              <li>
                <strong>Safari:</strong>{' '}
                <a
                  href="https://support.apple.com/kb/ph21411"
                  className="text-emerald-600 hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  support.apple.com/kb/ph21411
                </a>
              </li>
              <li>
                <strong>Microsoft Edge:</strong>{' '}
                <a
                  href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09"
                  className="text-emerald-600 hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  support.microsoft.com/microsoft-edge
                </a>
              </li>
            </ul>

            <p className="text-gray-700 mb-4">
              <strong>Notă:</strong> Dezactivarea cookie-urilor esențiale (connect.sid) va afecta funcționarea platformei (nu veți putea
              plasa comenzi).
            </p>

            {/* 12-15: Minori, Modificări, Contact, ANSPDCP - similar cu original dar actualizat */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">12. Minori</h2>
            <p className="text-gray-700 mb-4">
              Serviciile noastre nu sunt destinate persoanelor sub <strong>18 ani</strong>. Nu colectăm în mod intenționat date cu
              caracter personal de la minori fără consimțământul părinților/reprezentanților legali. Dacă descoperiți că un minor a
              furnizat date fără acordul dumneavoastră, vă rugăm să ne contactați imediat la dpo@cf-extras.ro pentru a proceda la ștergerea acestora.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">13. Modificări ale Politicii de Confidențialitate</h2>
            <p className="text-gray-700 mb-4">
              Ne rezervăm dreptul de a actualiza această Politică de Confidențialitate pentru a reflecta schimbări în legislație,
              servicii oferite, procesatori sau practici de prelucrare a datelor.
            </p>
            <p className="text-gray-700 mb-4">
              <strong>Modificările vor fi comunicate prin:</strong>
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Publicarea versiunii actualizate pe această pagină (cu data ultimei actualizări afișată sus)</li>
              <li>Email către utilizatorii cu cont activ (pentru modificări majore care afectează drepturile dumneavoastră)</li>
              <li>Banner pe pagina principală a platformei (pentru modificări esențiale)</li>
            </ul>
            <p className="text-gray-700 mb-4">
              Modificările intră în vigoare <strong>imediat după publicare</strong>. Utilizarea continuă a platformei după publicarea
              modificărilor constituie acceptarea noilor termeni. Vă recomandăm să verificați periodic această pagină.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">14. Contact pentru Exercitarea Drepturilor GDPR</h2>
            <p className="text-gray-700 mb-4">
              Pentru orice întrebări, solicitări sau exercitarea drepturilor GDPR, ne puteți contacta la:
            </p>

            <div className="bg-blue-50 p-6 rounded-lg border-2 border-blue-200 mb-6">
              <p className="text-gray-700 mb-2">
                <strong>BITCRAFTERS SRL</strong>
              </p>
              <p className="text-gray-700">
                <strong>Email GDPR:</strong>{' '}
                <a href="mailto:dpo@cf-extras.ro" className="text-emerald-600 hover:underline font-semibold">
                  dpo@cf-extras.ro
                </a>
              </p>
              <p className="text-gray-700">
                <strong>Telefon:</strong>{' '}
                <a href="tel:+40756888282" className="text-emerald-600 hover:underline font-semibold">
                  +40756888282
                </a>
              </p>
              <p className="text-gray-700">
                <strong>Adresă:</strong> Str. Fratii Golescu 4, București, Sector 1
              </p>
              <p className="text-gray-700 mt-2">
                <strong>Termen răspuns:</strong> maxim 30 zile de la primirea solicitării
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              15. Contact Autoritatea Națională de Supraveghere (ANSPDCP)
            </h2>
            <p className="text-gray-700 mb-4">
              Dacă considerați că drepturile dumneavoastră GDPR au fost încălcate, puteți depune o plângere la:
            </p>

            <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-300 mb-6">
              <p className="text-gray-700 mb-2">
                <strong>Autoritatea Națională de Supraveghere a Prelucrării Datelor cu Caracter Personal (ANSPDCP)</strong>
              </p>
              <p className="text-gray-700">
                <strong>Adresă:</strong> B-dul G-ral. Gheorghe Magheru Nr. 28-30, Sector 1, București, Cod poștal 010336, România
              </p>
              <p className="text-gray-700">
                <strong>Telefon:</strong> +40 318 059 211, +40 318 059 212
              </p>
              <p className="text-gray-700">
                <strong>Fax:</strong> +40 318 059 602
              </p>
              <p className="text-gray-700">
                <strong>Email:</strong>{' '}
                <a href="mailto:anspdcp@dataprotection.ro" className="text-emerald-600 hover:underline">
                  anspdcp@dataprotection.ro
                </a>
              </p>
              <p className="text-gray-700">
                <strong>Website:</strong>{' '}
                <a
                  href="https://www.dataprotection.ro"
                  className="text-emerald-600 hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  www.dataprotection.ro
                </a>
              </p>
            </div>

            <div className="bg-green-50 border-l-4 border-green-500 p-6 mt-8">
              <p className="text-green-900 font-semibold mb-2"><CheckCircle2 className="w-5 h-5 inline-block align-text-bottom mr-1" aria-hidden="true" />Ne angajăm să respectăm confidențialitatea datelor dumneavoastră</p>
              <p className="text-green-800 text-sm">
                Protecția datelor cu caracter personal este o prioritate pentru noi. Dacă aveți întrebări, nelămuriri sau îngrijorări cu
                privire la modul în care prelucrăm datele dumneavoastră, vă rugăm să nu ezitați să ne contactați la contact@cf-extras.ro. Vom răspunde în maxim 30 zile.
              </p>
            </div>

            <div className="mt-6 p-4 bg-gray-100 rounded-lg">
              <p className="text-sm text-gray-600 text-center">
                Prin utilizarea platformei <strong>cf-extras.ro</strong>, confirmați că ați citit, înțeles și acceptat
                prezenta Politică de Confidențialitate.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PoliticaConfidentialitate;
